/**
 * GranulloButton — The canonical button component.
 *
 * CANON (doctrine-locked):
 *   Shape      : sharp-edged (rounded-md) by default, pill (rounded-full) for primary
 *   Hover      : translateY(-1px) + elevated shadow + trailing icon slides 2px right
 *   Variants   : primary | secondary | ghost | destructive | link
 *   Sizes      : sm (32px) | md (40px) | lg (48px)
 *
 * USAGE:
 *   <GranulloButton variant="primary" size="md" icon={ArrowRight}>Try it</GranulloButton>
 *   <GranulloButton as="a" href="/pricing" variant="secondary">View pricing</GranulloButton>
 *   <GranulloButton to="/workspace" variant="ghost" icon={Layers}>Explore</GranulloButton>
 *
 * Every usage automatically receives data-testid if a `testId` prop is passed OR
 * falls back to data-testid="granullo-button" so tests can always find it.
 */
import React from 'react';
import { Link } from 'react-router-dom';

const VARIANT_CLASS = {
  primary: 'gb-primary gb-pill',        // pill shape for primary CTAs
  secondary: 'gb-secondary',
  ghost: 'gb-ghost',
  destructive: 'gb-destructive',
  link: 'gb-link',
};

const SIZE_CLASS = {
  sm: 'gb-size-sm',
  md: 'gb-size-md',
  lg: 'gb-size-lg',
};

export const GranulloButton = React.forwardRef(function GranulloButton(
  {
    variant = 'primary',
    size = 'md',
    icon: Icon,
    iconPosition = 'right',
    children,
    className = '',
    disabled = false,
    loading = false,
    as,
    to,
    href,
    testId,
    type = 'button',
    ...props
  },
  ref
) {
  const variantClass = VARIANT_CLASS[variant] ?? VARIANT_CLASS.primary;
  const sizeClass = variant === 'link' ? '' : SIZE_CLASS[size] ?? SIZE_CLASS.md;
  const iconHoverClass = Icon ? `gb-has-icon gb-icon-${iconPosition}` : '';

  const composedClass = [
    'gb-btn',
    variantClass,
    sizeClass,
    iconHoverClass,
    disabled || loading ? 'gb-disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {Icon && iconPosition === 'left' && (
        <Icon className="gb-icon gb-icon-leading" aria-hidden />
      )}
      <span className="gb-label">{loading ? 'Loading…' : children}</span>
      {Icon && iconPosition === 'right' && (
        <Icon className="gb-icon gb-icon-trailing" aria-hidden />
      )}
    </>
  );

  const commonProps = {
    ref,
    className: composedClass,
    'data-testid': testId ?? 'granullo-button',
    'data-variant': variant,
    'data-size': size,
    'aria-disabled': disabled || loading || undefined,
    ...props,
  };

  // react-router Link
  if (to) {
    return (
      <Link to={to} {...commonProps}>
        {content}
      </Link>
    );
  }

  // Anchor
  if (as === 'a' || href) {
    return (
      <a href={href} {...commonProps}>
        {content}
      </a>
    );
  }

  // Native button
  return (
    <button type={type} disabled={disabled || loading} {...commonProps}>
      {content}
    </button>
  );
});

export default GranulloButton;
